import React, { useState } from 'react';
import { AlertCircle, Check, Loader2, Trash2, UserCircle, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { VendorOrgLinkedPortalUser, VendorOrgLinkedProject } from '../../../utils/vendorOrgSummary';
import { vendorLinkedProjectHubPath } from '../../../utils/vendorOrgSummary';
import { workTypeLabel } from '../../../utils/vendorHelpers';

export interface VendorDeletePanelProps {
  vendorName: string;
  linkedProjects: VendorOrgLinkedProject[];
  linkedPortalUsers: VendorOrgLinkedPortalUser[];
  busy: boolean;
  onDelete: () => Promise<void>;
}

const VendorDeletePanel: React.FC<VendorDeletePanelProps> = ({
  vendorName,
  linkedProjects,
  linkedPortalUsers,
  busy,
  onDelete,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const projectsClear = linkedProjects.length === 0;
  const portalUsersClear = linkedPortalUsers.length === 0;
  const canDelete = projectsClear && portalUsersClear;

  const handleConfirm = async () => {
    setError(null);
    try {
      await onDelete();
      setConfirmOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete this vendor.');
      setConfirmOpen(false);
    }
  };

  return (
    <>
      <section className="vm-ops-section vm-ops-section--danger">
        <h3 className="vm-ops-section-title">Delete vendor</h3>
        <p className="vm-ops-hint">
          Permanently removes this company from the vendor directory. Pending portal invites for this vendor are
          deleted automatically.
        </p>

        <ul className="vm-delete-checklist" aria-label="Delete requirements">
          <li className={`vm-delete-checklist-item ${projectsClear ? 'ok' : 'blocked'}`}>
            {projectsClear ? <Check size={14} aria-hidden /> : <X size={14} aria-hidden />}
            <span>
              {projectsClear
                ? 'No active project assignments'
                : `${linkedProjects.length} project assignment${linkedProjects.length === 1 ? '' : 's'} still active`}
            </span>
          </li>
          <li className={`vm-delete-checklist-item ${portalUsersClear ? 'ok' : 'blocked'}`}>
            {portalUsersClear ? <Check size={14} aria-hidden /> : <X size={14} aria-hidden />}
            <span>
              {portalUsersClear
                ? 'No linked portal login accounts'
                : `${linkedPortalUsers.length} portal account${linkedPortalUsers.length === 1 ? '' : 's'} still linked`}
            </span>
          </li>
        </ul>

        {!projectsClear && (
          <div className="vm-delete-blocker-detail">
            <p className="vm-delete-blocker-title">Remove project assignments first</p>
            <ul className="vm-delete-blocker-list">
              {linkedProjects.map((project) => {
                const hubPath = vendorLinkedProjectHubPath(project);
                const label = (
                  <>
                    {project.dealTitle}
                    <span className="vm-cap-badge vm-cap-badge--sm">{workTypeLabel(project.workType)}</span>
                  </>
                );
                return (
                  <li key={project.rfqProjectId}>
                    {hubPath ? (
                      <Link to={hubPath} className="vm-delete-blocker-link">
                        {label}
                      </Link>
                    ) : (
                      <span className="vm-delete-blocker-link vm-delete-blocker-link--static">{label}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {!portalUsersClear && (
          <div className="vm-delete-blocker-detail">
            <p className="vm-delete-blocker-title">Remove linked portal accounts first</p>
            <p className="vm-ops-hint vm-delete-blocker-hint">
              Open <strong>Settings → Users</strong>, then deactivate or delete each account below (or reassign it to
              another vendor).
            </p>
            <ul className="vm-delete-portal-users">
              {linkedPortalUsers.map((account) => (
                <li key={account.uid} className="vm-delete-portal-user">
                  <UserCircle size={16} aria-hidden className="vm-delete-portal-user-icon" />
                  <div className="vm-delete-portal-user-main">
                    <span className="vm-delete-portal-user-name">{account.displayName}</span>
                    <span className="vm-delete-portal-user-email">{account.email}</span>
                  </div>
                  <span className={`vm-status-pill ${account.isActive ? 'vm-status-pill--accepted' : ''}`}>
                    {account.isActive ? 'Active' : 'Inactive'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {error && (
          <div className="vm-delete-error" role="alert">
            <AlertCircle size={14} aria-hidden /> {error}
          </div>
        )}

        <div className="vm-ops-actions">
          <button
            type="button"
            className="vm-delete-vendor-btn"
            disabled={busy || !canDelete}
            title={
              !canDelete
                ? 'Resolve the items above before deleting this vendor'
                : `Delete ${vendorName}`
            }
            onClick={() => {
              setError(null);
              setConfirmOpen(true);
            }}
          >
            <Trash2 size={13} aria-hidden />
            {busy ? 'Deleting…' : 'Delete vendor'}
          </button>
        </div>
      </section>

      {confirmOpen && (
        <div
          className="ops-modal-overlay vm-delete-confirm-overlay"
          role="presentation"
          onClick={() => {
            if (!busy) setConfirmOpen(false);
          }}
        >
          <div
            className="ops-modal vm-delete-confirm-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="vm-delete-confirm-title"
            aria-describedby="vm-delete-confirm-desc"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="ops-modal-header">
              <h3 id="vm-delete-confirm-title">Delete vendor?</h3>
              <button
                type="button"
                className="ops-modal-close"
                aria-label="Close"
                disabled={busy}
                onClick={() => setConfirmOpen(false)}
              >
                <X size={16} aria-hidden />
              </button>
            </header>
            <div className="ops-modal-body">
              <p id="vm-delete-confirm-desc" className="vm-delete-confirm-lead">
                <strong>{vendorName}</strong> will be permanently removed from the vendor directory. Pending portal
                invites for this company are deleted. This cannot be undone.
              </p>
            </div>
            <footer className="ops-modal-footer">
              <button
                type="button"
                className="ops-hide-completed-btn"
                disabled={busy}
                onClick={() => setConfirmOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="vm-delete-vendor-btn"
                disabled={busy}
                onClick={() => void handleConfirm()}
              >
                {busy ? (
                  <>
                    <Loader2 size={14} className="vm-delete-spin" aria-hidden /> Deleting…
                  </>
                ) : (
                  <>
                    <Trash2 size={13} aria-hidden /> Delete vendor
                  </>
                )}
              </button>
            </footer>
          </div>
        </div>
      )}
    </>
  );
};

export default VendorDeletePanel;
